import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  deleteMedia,
  getChallenges,
  getMediaDetail,
  getMediaPage,
  updateMedia,
  type GalleryFilter,
} from '../api/confetti'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { LoadingScreen } from '../components/LoadingScreen'
import { Sheet } from '../components/Sheet'
import { PlayIcon, TargetIcon } from '../components/icons'
import { formatBytes, formatMoment, formatTime } from '../lib/format'
import { useSwipe } from '../lib/useSwipe'
import { useSession } from '../session/context'
import type { GalleryTab } from '../components/FilterTabs'

function tabFrom(raw: string | null): GalleryTab {
  return raw === 'mijn' || raw === 'videos' ? raw : 'alles'
}

/**
 * Ronde 12 en 13 — één foto of video bekijken.
 *
 * Het enige donkere scherm van de app: het beeld moet het werk doen en alles
 * eromheen moet wegvallen. Op mobiel liggen de kop en de filmstrip als
 * verlopen over het beeld heen; vanaf lg staan ze erboven en eronder, met
 * pijlen links en rechts en de teller "142 van 312".
 */
export function MediaViewerPage() {
  const { slug, event, guest } = useSession()
  const { mediaId = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const tab = tabFrom(params.get('tab'))
  const guestId = guest?.guestId ?? ''

  // Dit scherm staat buiten AlbumLayout, dus de deur die daar dicht gaat bij een
  // afgesloten feest zit hier niet. Een bewaarde link naar één foto hoort net zo
  // goed terug te sturen als een link naar de galerij.
  const shutOut = event.isPrivate && !event.viewerIsOwner

  const filter: GalleryFilter =
    tab === 'mijn' ? { guestId } : tab === 'videos' ? { kind: 'Video' } : {}

  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [pickChallenge, setPickChallenge] = useState(false)

  const detail = useQuery({
    queryKey: ['media-detail', slug, mediaId, tab, guestId],
    queryFn: ({ signal }) => getMediaDetail(slug, mediaId, filter, signal),
  })

  const challenges = useQuery({
    queryKey: ['challenges', slug, guestId],
    queryFn: ({ signal }) => getChallenges(slug, guestId, signal),
    enabled: pickChallenge,
  })

  const item = detail.data

  const back = () => navigate(`/f/${slug}/album${tab === 'alles' ? '' : `?tab=${tab}`}`)

  const goTo = (id: string | null) => {
    if (id) {
      navigate(`/f/${slug}/album/${id}${tab === 'alles' ? '' : `?tab=${tab}`}`, {
        replace: true,
      })
    }
  }

  // Vegen op een telefoon doet hetzelfde als de pijltjes op desktop. Die
  // pijlknoppen staan links en rechts tegen de rand en zijn met één duim niet
  // te halen; vegen is wat je daar toch al probeert.
  const swipe = useSwipe(
    () => goTo(item?.nextId ?? null),
    () => goTo(item?.previousId ?? null),
  )

  // De pijltjestoetsen op desktop, en Escape om terug te gaan.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (menuOpen || confirmDelete || pickChallenge) {
        return
      }

      if (event.key === 'ArrowLeft') {
        goTo(item?.previousId ?? null)
      } else if (event.key === 'ArrowRight') {
        goTo(item?.nextId ?? null)
      } else if (event.key === 'Escape') {
        back()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  })

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['media', slug] })
    await queryClient.invalidateQueries({ queryKey: ['event', slug] })
    await queryClient.invalidateQueries({ queryKey: ['guest', slug] })
    await queryClient.invalidateQueries({ queryKey: ['challenges', slug] })
  }

  const link = useMutation({
    mutationFn: (challengeId: string | null) =>
      updateMedia(slug, mediaId, { guestId, challengeId }),
    onSuccess: async () => {
      setPickChallenge(false)
      await detail.refetch()
      await invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteMedia(slug, mediaId, guestId),
    onSuccess: async () => {
      await invalidate()
      back()
    },
  })

  if (shutOut) {
    return <Navigate to={`/f/${slug}`} replace />
  }

  if (detail.isPending) {
    return (
      <div className="bg-viewer min-h-full">
        <LoadingScreen label="Foto ophalen…" />
      </div>
    )
  }

  if (!item) {
    return (
      <main className="bg-viewer text-viewer-ink flex min-h-full flex-col items-center justify-center gap-5 px-8 text-center">
        <p className="font-display text-3xl">Deze foto is er niet meer</p>
        <button type="button" onClick={back} className="text-viewer-ink-muted text-sm underline">
          Terug naar het album
        </button>
      </main>
    )
  }

  const mine = item.guestId === guestId
  const challengeLabel = item.challengeTitle ? `Challenge · ${item.challengeTitle}` : null

  return (
    <main className="bg-viewer text-viewer-ink lg:bg-viewer-wide flex h-full flex-col">
      {/* --- Kop -------------------------------------------------------- */}
      {/*
        Een echte balk en niet iets dat over de foto zweeft. Met alleen een
        gradiënt eronder stonden de knoppen op een lichte foto in het niets: de
        terugknop, de naam en de drie puntjes waren nauwelijks te onderscheiden.
        Nu staat de kop in de flow, op zijn eigen zwart, en krijgt het beeld de
        hoogte die overblijft.
      */}
      <header className="bg-viewer relative z-20 flex shrink-0 items-center justify-between gap-4 px-4.5 pt-5 pb-4 lg:px-6.5 lg:py-5.5">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            aria-label="Terug naar het album"
            onClick={back}
            className="bg-viewer-ink/22 hover:bg-viewer-ink/32 flex size-9.5 items-center justify-center rounded-full text-[17px] transition-colors lg:size-10"
          >
            <span className="lg:hidden">←</span>
            <span className="hidden lg:inline">✕</span>
          </button>

          {/* Op mobiel staat de naam gecentreerd, op desktop naast de knop. */}
          <div className="hidden text-left lg:block">
            <p className="text-[15px] font-medium">{mine ? 'Jij' : item.guestName}</p>
            <p className="text-viewer-ink-muted mt-0.5 text-[13px]">
              {formatMoment(item.uploadedAtUtc)}
            </p>
          </div>
        </div>

        <div className="text-center lg:hidden">
          <p className="text-sm font-medium">{mine ? 'Jij' : item.guestName}</p>
          <p className="text-viewer-ink-muted mt-0.5 text-xs">{formatMoment(item.uploadedAtUtc)}</p>
        </div>

        <div className="flex items-center gap-4">
          {challengeLabel && (
            <span className="bg-accent/92 text-accent-ink hidden items-center gap-2 rounded-full px-4 py-2 text-sm font-medium lg:flex">
              <TargetIcon className="size-3" />
              {challengeLabel}
            </span>
          )}

          <span className="text-viewer-ink-muted hidden text-[13px] lg:inline">
            {item.position} van {item.totalCount}
          </span>

          {mine ? (
            <button
              type="button"
              aria-label="Meer opties"
              onClick={() => setMenuOpen(true)}
              className="bg-viewer-ink/22 hover:bg-viewer-ink/32 lg:bg-viewer-ink/25 flex size-9.5 items-center justify-center rounded-full text-[17px] transition-colors lg:size-10"
            >
              ⋯
            </button>
          ) : (
            <span className="size-9.5 lg:hidden" />
          )}
        </div>
      </header>

      {/* --- Het beeld --------------------------------------------------- */}
      <div
        {...swipe}
        className="relative flex min-h-0 flex-1 items-center justify-center lg:px-24 lg:pb-6.5"
      >
        {item.previousId && <ArrowButton side="left" onClick={() => goTo(item.previousId)} />}
        {item.nextId && <ArrowButton side="right" onClick={() => goTo(item.nextId)} />}

        {item.kind === 'Video' && item.originalUrl ? (
          <video
            key={item.id}
            src={item.originalUrl}
            poster={item.webUrl}
            controls
            playsInline
            className="h-full max-h-full w-full object-contain lg:rounded-md"
          />
        ) : (
          <img
            src={item.webUrl}
            alt={`Van ${item.guestName}`}
            className="h-full max-h-full w-full object-contain lg:rounded-md"
          />
        )}

        {challengeLabel && (
          <span className="bg-accent/92 text-accent-ink absolute top-4 left-4.5 flex items-center gap-2 rounded-full px-3.5 py-1.75 text-[13px] font-medium lg:hidden">
            <TargetIcon className="size-3" />
            {challengeLabel}
          </span>
        )}

        {item.kind === 'Video' && !item.originalUrl && (
          <span className="bg-viewer-ink/90 text-viewer pointer-events-none absolute flex size-18 items-center justify-center rounded-full">
            <PlayIcon className="size-8" />
          </span>
        )}
      </div>

      {/* --- De filmstrip onderaan (ronde 12A en 13A) -------------------- */}
      <Filmstrip currentId={item.id} tab={tab} onPick={goTo} />

      {/* --- Het ⋯-menu op je eigen foto (ronde 12C en 13C) --------------- */}
      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} labelledBy="eigen-media">
        <div className="border-line-soft text-ink flex items-center gap-3 border-b pb-3.5">
          <div className="placeholder-warm rounded-thumb size-11.5 shrink-0 overflow-hidden">
            <img src={item.webUrl} alt="" className="h-full w-full object-cover" />
          </div>

          <div className="min-w-0">
            <p id="eigen-media" className="truncate text-[15px] font-semibold">
              {item.originalFileName}
            </p>
            <p className="text-ink-soft mt-0.5 text-[13px]">
              {formatBytes(item.sizeBytes)} · {formatTime(item.uploadedAtUtc)}
            </p>
          </div>
        </div>

        <div className="text-ink flex flex-col pt-1">
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false)
              setPickChallenge(true)
            }}
            className="flex items-center gap-3.5 px-0.5 py-3.5 text-left text-base"
          >
            <span aria-hidden="true" className="text-ink-soft w-5 text-center">
              ◎
            </span>
            {item.challengeId ? 'Challenge wijzigen' : 'Koppel aan een challenge'}
          </button>

          <button
            type="button"
            onClick={() => {
              setMenuOpen(false)
              setConfirmDelete(true)
            }}
            className="text-accent flex items-center gap-3.5 px-0.5 py-3.5 text-left text-base"
          >
            <span aria-hidden="true" className="w-5 text-center">
              ✕
            </span>
            Verwijderen uit het album
          </button>
        </div>
      </Sheet>

      {/* --- De challenge kiezen ---------------------------------------- */}
      <Sheet
        open={pickChallenge}
        onClose={() => setPickChallenge(false)}
        labelledBy="kies-challenge"
      >
        <h2 id="kies-challenge" className="font-display text-ink text-[30px] leading-tight">
          Bij welke challenge hoort dit?
        </h2>
        <p className="text-ink-soft mt-2 text-sm">Je foto blijft ook in de galerij staan.</p>

        <div className="text-ink mt-5 flex flex-col gap-2">
          {item.challengeId && (
            <button
              type="button"
              onClick={() => link.mutate(null)}
              className="rounded-row border-line text-accent border px-4 py-3.5 text-left text-[15px]"
            >
              Loskoppelen
            </button>
          )}

          {(challenges.data?.challenges ?? [])
            .filter((challenge) => !challenge.isExpired && !challenge.isLocked)
            .map((challenge) => (
              <button
                key={challenge.id}
                type="button"
                onClick={() => link.mutate(challenge.id)}
                className={
                  'rounded-row flex items-center gap-3 border px-4 py-3.5 text-left text-[15px] ' +
                  (challenge.id === item.challengeId
                    ? 'border-accent bg-tint'
                    : 'border-line-row bg-surface')
                }
              >
                <span aria-hidden="true">{challenge.emoji ?? '◎'}</span>
                <span className="min-w-0 flex-1 truncate">{challenge.title}</span>
                <span className="text-ink-faint shrink-0 text-[13px]">
                  {challenge.kind === 'Video'
                    ? 'Video'
                    : challenge.kind === 'Photo'
                      ? 'Foto'
                      : 'Allebei'}
                </span>
              </button>
            ))}

          {challenges.isPending && <p className="text-ink-soft text-sm">Challenges ophalen…</p>}
        </div>
      </Sheet>

      <ConfirmDialog
        open={confirmDelete}
        title="Verwijderen uit het album?"
        detail={`${item.originalFileName} verdwijnt voor iedereen en is daarna niet meer terug te halen.`}
        confirmLabel="Ja, verwijderen"
        cancelLabel="Laat staan"
        onConfirm={() => {
          setConfirmDelete(false)
          remove.mutate()
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </main>
  )
}

function ArrowButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Vorige' : 'Volgende'}
      onClick={onClick}
      className={
        'absolute top-1/2 z-10 hidden size-13 -translate-y-1/2 items-center justify-center ' +
        'bg-viewer-ink/12 text-viewer-ink rounded-full text-xl lg:flex ' +
        (side === 'left' ? 'left-6.5' : 'right-6.5')
      }
    >
      {side === 'left' ? '‹' : '›'}
    </button>
  )
}

/**
 * De strook miniaturen onderaan.
 *
 * Leest dezelfde infinite query als de galerij, dus als je hier via een tegel
 * bent gekomen staat alles al in de cache en is dit gratis. Bij een gedeelde
 * link haalt hij de eerste pagina alsnog op — dan is de strook wat korter dan
 * in het ontwerp, maar dat is beter dan een lege balk.
 */
function Filmstrip({
  currentId,
  tab,
  onPick,
}: {
  currentId: string
  tab: GalleryTab
  onPick: (id: string) => void
}) {
  const { slug, guest } = useSession()
  const guestId = guest?.guestId ?? ''

  const filter: GalleryFilter =
    tab === 'mijn' ? { guestId } : tab === 'videos' ? { kind: 'Video' } : {}

  const media = useInfiniteQuery({
    queryKey: ['media', slug, tab, guestId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => getMediaPage(slug, filter, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor,
  })

  const window = useMemo(() => {
    const items = media.data?.pages.flatMap((page) => page.items) ?? []
    const index = items.findIndex((item) => item.id === currentId)

    if (index < 0) {
      return []
    }

    // Drie aan elke kant: dat is de vijf van ronde 12A met wat lucht voor de
    // bredere strook van ronde 13A, en het snijdt vanzelf af aan de randen.
    return items.slice(Math.max(0, index - 3), index + 4)
  }, [media.data, currentId])

  if (window.length < 2) {
    return <div className="h-6.5 shrink-0" />
  }

  return (
    <div className="scroll-x-clean flex shrink-0 justify-center gap-1.5 px-4.5 pb-6.5 lg:gap-2 lg:pb-6.5">
      {window.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-label={`Van ${item.guestName}`}
          aria-current={item.id === currentId}
          onClick={() => onPick(item.id)}
          className={
            'size-10.5 shrink-0 overflow-hidden rounded-[7px] transition-opacity lg:size-14.5 ' +
            (item.id === currentId ? 'ring-viewer-ink ring-2' : 'opacity-55')
          }
        >
          <img src={item.webUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  )
}
